import {components, GroupBase, OptionProps, SingleValue, SingleValueProps} from "react-select";

import {Select} from "@/components/select/Select.tsx";
import {SelectProps} from "@/components/select/Select.types.ts";

import {HorizontalDivider} from "../HorizontalDivider.tsx";

interface SelectValue<T> {
  value: keyof T | "allOption";
}

const SelectTableFilterValue = <T,>({children, data, ...props}: SingleValueProps<SelectValue<T>>) => {
  return (
    <components.SingleValue {...props} data={data}>
      <span>{children}</span>
    </components.SingleValue>
  );
};

const SelectTableFilterOption = <T,>({children, data, ...props}: OptionProps<SelectValue<T>>) => {
  return (
    <div>
      <components.Option {...props} data={data}>
        <span>{children}</span>
      </components.Option>
      {data.value === "allOption" && <HorizontalDivider className={"my-1"} colorClassName={"border-dark"} />}
    </div>
  );
};

interface SelectTableFilterProps<
  EnumOptions extends {[key: string]: string},
  OptionType = SelectValue<EnumOptions>,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> extends Omit<SelectProps<OptionType, IsMulti, GroupType>, "value" | "onChange"> {
  onChange: (value: keyof EnumOptions | null) => void;
  enumOptions: EnumOptions;
  allOption?: string;
  value: keyof EnumOptions | null;
}

export const SelectTableFilter = <T extends {[key: string]: string}>({
  onChange,
  enumOptions,
  allOption,
  value,
  ...rest
}: SelectTableFilterProps<T>) => {
  const options = Object.entries(enumOptions).map(([key]) => ({
    value: key,
  }));
  if (allOption) {
    options.unshift({value: "allOption"});
  }
  return (
    <Select
      value={value ? {value} : null}
      options={options}
      getOptionValue={(value) => value.value as string}
      getOptionLabel={(value) => {
        if (value.value === "allOption" && allOption) {
          return allOption;
        } else {
          return enumOptions[value.value];
        }
      }}
      onChange={(v: SingleValue<SelectValue<T>>) => {
        if (v) {
          if (v.value === "allOption") {
            onChange(null);
          } else {
            onChange(v.value);
          }
        } else {
          onChange(null);
        }
      }}
      components={{Option: SelectTableFilterOption, SingleValue: SelectTableFilterValue}}
      {...rest}
    />
  );
};
